import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Badge, Button, gtagEvent, vars } from '@agendor/ui';
import { Checkbox, CheckboxLabel } from '../components/Input';
import { WhatsAppConnectorStatus } from '../business-service';
import {
    connectorConnectionState,
    connectorDownloads,
    connectorTermsUrl,
    detectConnectorPlatform,
    isHandheldDevice,
    readConnectorArchitecture,
    readConnectorDevice,
} from '../whatsapp-connector';

export type ConnectionMethod = 'cloud_api' | 'connector';

const ChoiceList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const ChoiceCard = styled.button<{ selected: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    text-align: left;
    font: inherit;
    color: ${vars.foregroundColor};
    padding: 16px;
    border-radius: 8px;
    cursor: pointer;
    background: ${(p) => (p.selected ? vars.primaryColorLight : vars.backgroundColorContent)};
    /* Same selected state as the credit purchase tabs (CreditPurchaseModal). */
    border: 1px solid ${(p) => (p.selected ? vars.foregroundColor : vars.dividerColor)};
    transition: background-color 0.15s ease;

    &:hover {
        background: ${(p) => (p.selected ? vars.primaryColorLight : vars.backgroundColorBright)};
    }

    &:focus-visible {
        outline: 2px solid ${vars.accentColor};
        outline-offset: 2px;
    }
`;

const ChoiceTitle = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
`;

const ChoiceDescription = styled.span`
    font-size: 0.9rem;
    line-height: 1.45;
    opacity: 0.85;
`;

export const ConnectorRiskNotice = styled.div`
    padding: 12px 16px;
    background-color: ${vars.warningTintSoft};
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    font-size: 0.9rem;
    line-height: 1.5;
`;

// Same shape as the intake preview's numbered list (ServicesSection).
const Steps = styled.ol`
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Step = styled.li`
    padding-left: 4px;

    &::marker {
        font-weight: 600;
    }
`;

const StepBody = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
`;

const StepTitle = styled.span`
    font-size: 0.95rem;
    font-weight: 600;
    color: ${vars.foregroundColor};
`;

const StepHint = styled.span`
    font-size: 0.85rem;
    color: #666;
    line-height: 1.45;
`;

const HandheldNotice = styled.span`
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.45;
`;

const TermsLabel = styled(CheckboxLabel)`
    align-items: flex-start;
    width: auto;
    font-size: 0.9rem;
    line-height: 1.45;

    a {
        color: ${vars.accentColor};
    }
`;

const OtherDownloads = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    font-size: 0.85rem;

    a {
        color: ${vars.accentColor};
    }
`;

const pulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.4; }
    100% { opacity: 1; }
`;

const StatusDot = styled.span<{ tone: 'online' | 'waiting' | 'offline' }>`
    flex: none;
    width: 8px;
    height: 8px;
    margin-top: 0.45em;
    border-radius: 50%;
    background: ${(p) =>
        p.tone === 'online' ? vars.accentColor : p.tone === 'waiting' ? vars.warningColor : vars.dangerColor};
    animation: ${(p) => (p.tone === 'offline' ? 'none' : pulse)} 2s ease-in-out infinite;
`;

const StatusBox = styled.div.attrs({ role: 'status', 'aria-live': 'polite' })<{
    tone: 'online' | 'waiting' | 'offline';
}>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid ${vars.dividerColor};
    background: ${(p) =>
        p.tone === 'online' ? vars.successTintSoft : p.tone === 'waiting' ? vars.warningTintSoft : vars.dangerTintSoft};
    font-size: 0.9rem;
    line-height: 1.45;
`;

const StatusRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-weight: 600;
`;

const DisconnectButton = styled(Button)`
    align-self: flex-start;
`;

export function ConnectionChoice(props: { selected: ConnectionMethod; onSelect: (method: ConnectionMethod) => void }) {
    const { selected, onSelect } = props;
    return (
        <ChoiceList role="radiogroup" aria-label="Forma de conexão">
            <ChoiceCard
                type="button"
                role="radio"
                aria-checked={selected === 'cloud_api'}
                selected={selected === 'cloud_api'}
                onClick={() => onSelect('cloud_api')}
                data-testid="connection-choice-official"
            >
                <ChoiceTitle>
                    Conexão oficial <Badge tone="success">Recomendada</Badge>
                </ChoiceTitle>
                <ChoiceDescription>
                    Autorizada pela Meta, dona do WhatsApp. Mais segura e estável, funciona 24h sem depender do seu
                    computador. Exige um cartão de crédito na Meta, que cobra ~R$&nbsp;0,04 por lembrete e por resposta
                    além das gratuitas do mês.
                </ChoiceDescription>
            </ChoiceCard>
            <ChoiceCard
                type="button"
                role="radio"
                aria-checked={selected === 'connector'}
                selected={selected === 'connector'}
                onClick={() => onSelect('connector')}
                data-testid="connection-choice-connector"
            >
                <ChoiceTitle>Conexão pelo computador</ChoiceTitle>
                <ChoiceDescription>
                    Instale o OrganizaBot Conector no seu computador e conecte lendo um QR code com o celular. Sem
                    aprovação da Meta e sem custo por mensagem, mas o assistente só responde enquanto o computador
                    estiver ligado e com internet. É uma conexão não oficial: existe risco de o WhatsApp restringir ou
                    banir o número.
                </ChoiceDescription>
            </ChoiceCard>
        </ChoiceList>
    );
}

/** The owner's acknowledgement of the risks, required once before connecting through the computer. */
export function ConnectorTermsCheckbox(props: { checked: boolean; onChange: (checked: boolean) => void }) {
    const { checked, onChange } = props;
    return (
        <TermsLabel>
            <Checkbox
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                required
                data-testid="connector-terms-checkbox"
            />
            <span>
                Li e aceito os riscos da conexão pelo computador e os{' '}
                <a href={connectorTermsUrl} target="_blank" rel="noreferrer">
                    Termos de Uso
                </a>
            </span>
        </TermsLabel>
    );
}

export function ConnectorDownloadButtons() {
    const [device, setDevice] = useState(readConnectorDevice);
    useEffect(() => {
        let cancelled = false;
        readConnectorArchitecture().then((architecture) => {
            if (!cancelled && architecture !== null) setDevice((current) => ({ ...current, architecture }));
        });
        return () => {
            cancelled = true;
        };
    }, []);

    const downloads = connectorDownloads();
    const isHandheld = isHandheldDevice(device);
    const detected = detectConnectorPlatform(device);
    const primary = downloads.find((download) => download.platform === detected) ?? null;
    const others = downloads.filter((download) => download !== primary);
    return (
        <>
            {isHandheld && (
                <HandheldNotice data-testid="connector-handheld-notice">
                    Faça isso no computador que vai ficar ligado.
                </HandheldNotice>
            )}
            {primary && (
                <Button
                    primary
                    as="a"
                    href={primary.url}
                    style={{ width: 'fit-content' }}
                    onClick={() => gtagEvent('connector_download_click', { platform: primary.platform })}
                    data-testid="connector-download"
                >
                    Baixar para {primary.label}
                </Button>
            )}
            <OtherDownloads>
                {primary && <span>Outros sistemas:</span>}
                {others.map((download) => (
                    <a
                        key={download.platform}
                        href={download.url}
                        onClick={() => gtagEvent('connector_download_click', { platform: download.platform })}
                    >
                        {download.label}
                    </a>
                ))}
            </OtherDownloads>
            {!isHandheld && (
                <StepHint data-testid="connector-windows-hint">
                    No Windows, se aparecer “O Windows protegeu o computador”, clique em “Mais informações” e depois em
                    “Executar assim mesmo”.
                </StepHint>
            )}
        </>
    );
}

export function ConnectorSetup(props: {
    status: WhatsAppConnectorStatus | null;
    // The owner just approved a computer that hasn't come online yet.
    isApprovalPending: boolean;
    onDisconnect: () => void;
    isDisconnecting: boolean;
}) {
    const { status, isApprovalPending, onDisconnect, isDisconnecting } = props;
    return (
        <>
            <Steps>
                <Step>
                    <StepBody>
                        <StepTitle>Baixe e instale o OrganizaBot Conector</StepTitle>
                        <ConnectorDownloadButtons />
                        {/* Accepted on the choice screen already: a reminder, so the live status stays the only box. */}
                        <StepHint>
                            Lembre-se: é uma conexão não oficial com o WhatsApp, com risco de o WhatsApp restringir ou
                            banir o número.
                        </StepHint>
                    </StepBody>
                </Step>
                <Step>
                    <StepBody>
                        <StepTitle>Abra o app e clique em “Entrar com minha conta”</StepTitle>
                        <StepHint>
                            O navegador vai abrir este painel para você autorizar o computador. Confira se o código é o
                            mesmo que aparece no app.
                        </StepHint>
                    </StepBody>
                </Step>
                <Step>
                    <StepBody>
                        <StepTitle>Escaneie o QR code do app com o WhatsApp do negócio</StepTitle>
                        <StepHint>
                            No celular, abra o WhatsApp e toque em Aparelhos conectados → Conectar um aparelho.
                        </StepHint>
                    </StepBody>
                </Step>
            </Steps>

            {status === null && isApprovalPending && <AwaitingAppStatus />}
            {status && <ConnectorDeviceStatus status={status} />}
            {status && (
                <DisconnectButton onClick={onDisconnect} disabled={isDisconnecting}>
                    Desconectar este computador
                </DisconnectButton>
            )}
        </>
    );
}

/** Approved, but the app on that computer hasn't linked yet. */
function AwaitingAppStatus() {
    return (
        <StatusBox tone="waiting" data-testid="connector-status">
            <StatusRow>
                <StatusDot tone="waiting" />
                <span>Computador autorizado. Aguardando o app…</span>
            </StatusRow>
        </StatusBox>
    );
}

function ConnectorDeviceStatus(props: { status: WhatsAppConnectorStatus }) {
    const { status } = props;
    if (!status.online && status.lastSeenAt === null) return <AwaitingAppStatus />;
    if (status.online && status.refusal === 'phone_number_in_use' && status.whatsapp !== 'connected') {
        return (
            <StatusBox tone="offline" data-testid="connector-status">
                <StatusRow>
                    <StatusDot tone="offline" />
                    <span>Este número já está conectado a outra conta do OrganizaBot</span>
                </StatusRow>
                <span>Escaneie o QR code do app com o WhatsApp de outro número.</span>
            </StatusBox>
        );
    }
    if (!status.online) {
        return (
            <StatusBox tone="offline" data-testid="connector-status">
                <StatusRow>
                    <StatusDot tone="offline" />
                    <span>App fechado ou sem internet</span>
                </StatusRow>
                <span>O computador já está autorizado. Abra o OrganizaBot Conector nele para continuar.</span>
            </StatusBox>
        );
    }
    const waitingText =
        status.whatsapp === 'connecting'
            ? 'Conectando ao WhatsApp…'
            : status.whatsapp === 'connected'
              ? 'WhatsApp conectado. Finalizando a conexão…'
              : status.whatsapp === 'logged_out'
                ? 'O WhatsApp desconectou o computador. Escaneie o novo QR code no app.'
                : 'Aguardando você escanear o QR code no app.';
    return (
        <StatusBox tone="waiting" data-testid="connector-status">
            <StatusRow>
                <StatusDot tone="waiting" />
                <span>Computador autorizado e online</span>
            </StatusRow>
            <span>{waitingText}</span>
        </StatusBox>
    );
}

/** The live state of a number linked through the connector. */
export function ConnectorConnectionStatus(props: { invalidated: boolean; status: WhatsAppConnectorStatus | null }) {
    const { invalidated, status } = props;
    const state = connectorConnectionState({ invalidated, status });
    if (state === 'invalidated') {
        return (
            <StatusBox tone="offline" data-testid="connector-status">
                <StatusRow>
                    <StatusDot tone="offline" />
                    <span>O WhatsApp desconectou o computador</span>
                </StatusRow>
                <span>
                    Abra o OrganizaBot Conector e escaneie o novo QR code com o WhatsApp do negócio. Enquanto isso, o
                    assistente não responde seus clientes.
                </span>
            </StatusBox>
        );
    }
    if (state === 'offline') {
        return (
            <StatusBox tone="offline" data-testid="connector-status">
                <StatusRow>
                    <StatusDot tone="offline" />
                    <span>
                        Computador offline
                        {status?.lastSeenAt ? ` desde ${formatLastSeen(status.lastSeenAt)}` : ''}
                    </span>
                </StatusRow>
                <span>
                    O computador com o OrganizaBot Conector está desligado, sem internet ou com o app fechado. O
                    assistente volta a responder assim que ele ficar online.
                </span>
            </StatusBox>
        );
    }
    if (state === 'awaiting_app') return <AwaitingAppStatus />;
    if (state === 'pairing') {
        return (
            <StatusBox tone="waiting" data-testid="connector-status">
                <StatusRow>
                    <StatusDot tone="waiting" />
                    <span>Computador autorizado e online</span>
                </StatusRow>
                <span>Aguardando você escanear o QR code no app.</span>
            </StatusBox>
        );
    }
    if (state === 'reconnecting') {
        return (
            <StatusBox tone="waiting" data-testid="connector-status">
                <StatusRow>
                    <StatusDot tone="waiting" />
                    <span>Reconectando ao WhatsApp…</span>
                </StatusRow>
            </StatusBox>
        );
    }
    return (
        <StatusBox tone="online" data-testid="connector-status">
            <StatusRow>
                <StatusDot tone="online" />
                <span>Conectado pelo computador</span>
            </StatusRow>
            <span>Mantenha o computador ligado e com internet para o assistente responder seus clientes.</span>
        </StatusBox>
    );
}

function formatLastSeen(iso: string): string {
    const date = new Date(iso);
    const isToday = date.toDateString() === new Date().toDateString();
    const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (isToday) return time;
    return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} às ${time}`;
}
